
import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'
import {
    uploadAvatar,
    getCharacter,
    generateCharacters,
    saveAvatar,
} from '@/api/conversation'
import { AvatarSetting, Character, RandSeed } from '@/entities/avatar'
import { Option } from '@/entities/form'
import BaseFormRow from '@/components/BaseFormRow.vue'
import BaseSelectInput from '@/components/BaseSelectInput.vue'
import BaseButton from '@/components/BaseButton.vue'
import InputColor from './form/InputColor.vue'
import InputFileImage from './form/InputFileImage.vue'
import CharacterPicker from './CharacterPicker.vue'
import { getCDNUrl } from '@/utils/cdn'

@Component<AvatarEditor>({
    components: {
        BaseFormRow,
        BaseSelectInput,
        BaseButton,
        InputColor,
        InputFileImage,
        CharacterPicker,
    },
})
export default class AvatarEditor extends Vue {
    @Prop(String) public template!: string
    @Prop(String) public defaultColor!: string
    @Prop(String) public avatarUrl!: string
    @Prop(Object) public avatarSetting!: AvatarSetting

    getCDNUrl = getCDNUrl

    public url = ''
    public showPicker = true
    public color = ''
    public error: Error | null = null
    public typeOptions: Array<Option<string>> = [
        {
            value: 'avatar',
            label: 'Avatar',
        },
        {
            value: 'character',
            label: 'Character',
        },
    ]
    public type: string = this.typeOptions[0].value
    public isGeneratingCharacters = false
    public randseeds: RandSeed[] = []
    public characters: Character[] = []
    public character: Character | null = null

    public get options() {
        return [this.character].concat(this.characters)
    }

    public get canSave() {
        if (this.type === 'character' && !this.character) {
            return false
        }
        if (this.type === 'avatar' && !this.url) {
            return false
        }
        return true
    }

    public async mounted() {
        this.color =
            '#' +
            (this.avatarSetting.color || this.defaultColor || '333333').replace(
                /\#/,
                ''
            )
        if (
            this.avatarSetting.type &&
            this.typeOptions.find(
                (option) => option.value === this.avatarSetting.type
            )
        ) {
            this.type = this.avatarSetting.type
        }
        if (this.type === 'avatar') {
            this.url = this.avatarUrl
        }

        this.load()
    }

    public async load() {
        this.loadCharacters()
    }

    public async loadCharacters() {
        this.isGeneratingCharacters = true
        const { data } = await generateCharacters(this.template)

        this.randseeds = data
        this.characters = await this.renderCharacters(
            this.randseeds,
            this.color
        )

        this.isGeneratingCharacters = false
    }

    public async renderCharacters(randseeds, color): Promise<Character[]> {
        return await Promise.all(
            randseeds.map(([top, person]) =>
                this.getCharacter(top, person, color)
            ) as Character[]
        )
    }

    public async getCharacter(top, person, color): Promise<Character> {
        const {
            data: { url },
        } = await getCharacter(top, person, this.color)

        return {
            top,
            person,
            color: this.color,
            url,
        } as Character
    }

    public async upload(event: Event) {
        const files = (event.target as HTMLInputElement).files
        if (files && files[0]) {
            try {
                const { data } = await uploadAvatar(this.template, files[0])
                this.url = data.url
            } catch (err: any) {
                this.error = err
            }
        }
    }

    public async saveAvatar() {
        try {
            const avatarSetting = {
                type: this.type,
                color: this.color,
            } as AvatarSetting
            let avatarUrl = this.avatarUrl

            if (this.type === 'character') {
                if (!this.character) {
                    throw new Error()
                }

                avatarSetting.randseed = [
                    this.character.top,
                    this.character.person,
                ] as RandSeed
                avatarUrl = this.character.url
            }

            if (this.type === 'avatar') {
                avatarUrl = this.url
            }

            const { data } = await saveAvatar(
                this.template,
                avatarUrl,
                avatarSetting
            )

            this.$emit('change', {
                url: avatarUrl,
                setting: avatarSetting,
            })
        } catch (error: any) {
            this.error = error
        }
    }

    public async onChangeColor(value) {
        this.color = value

        this.characters = await this.renderCharacters(
            this.randseeds,
            this.color
        )
    }

    public onChangeCharacter(character: Character) {
        this.character = character
    }
}
